/* eslint-disable no-restricted-syntax */
import {
  BINARY_ICON,
  BORN_TYPE_ICON,
  GENDER_ICON,
  PARENT_TYPE_ICON,
  PEDIGREE_TYPE_ICON,
} from '@application/constants';
import {
  BUYER_SUPPORTED_PHONE_NUMBERS,
  COUNTRY_CODES_WITH_PHONE_LABELS,
  SUPPORTED_BUYER_COUNTRIES,
  SUPPORTED_COUNTRIES,
  SUPPORTED_PHONE_NUMBERS,
  SUPPORTED_REGIONS,
} from '@application/constants/countries';
import { LANGUAGE_CODES } from '@application/constants/language';
import type {
  Language,
  PedigreeType,
  PedigreeTypePresentation,
  Translate,
} from '@application/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AnimalSensitivityEnum,
  AnimalSizeEnum,
  AnimalTypeEnum,
  CountryCode,
  GenderEnum,
  ParentTypeEnum,
} from '@gql/graphql';
import type { SelectOption } from '@presentation/components';
import { capitalize, mapValues, pickBy, sortBy, uniqBy } from 'lodash/fp';
import { sortSelectOptionByLabel } from '.';

export const animalSizeSelectOptions = (translate: Translate): SelectOption<AnimalSizeEnum>[] => {
  const orderedKeys: AnimalSizeEnum[] = [
    AnimalSizeEnum.SMALL,
    AnimalSizeEnum.MEDIUM,
    AnimalSizeEnum.BIG,
  ];
  return orderedKeys.map(
    (animalSize) =>
      ({
        value: animalSize,
        label: translate(`enum.animalSize.${animalSize}`),
      } as SelectOption<AnimalSizeEnum>),
  );
};

export const AnimalSensitivitySelectOptions = (
  translate: Translate,
): SelectOption<AnimalSensitivityEnum>[] => {
  const orderedKeys: AnimalSensitivityEnum[] = [
    AnimalSensitivityEnum.DIGESTION,
    AnimalSensitivityEnum.GROWTH,
    AnimalSensitivityEnum.ITCHING,
    AnimalSensitivityEnum.MOBILITY,
    AnimalSensitivityEnum.STRESS,
    AnimalSensitivityEnum.NONE,
  ];
  return orderedKeys.map(
    (sensitivity) =>
      ({
        value: sensitivity,
        label: translate(`enum.sensitivities.${sensitivity}`),
      } as SelectOption<AnimalSensitivityEnum>),
  );
};

export const genderSelectOptions = (
  translate: Translate,
  options: { showOther?: boolean; showText?: boolean; showIcons?: boolean; isHuman?: boolean } = {
    showIcons: false,
    showText: true,
    showOther: false,
    isHuman: false,
  },
): SelectOption<GenderEnum>[] => {
  return Object.values(GenderEnum)
    .filter((gender: GenderEnum) => (options.showOther ? true : gender !== GenderEnum.OTHER))
    .map(
      (gender) =>
        ({
          value: gender,
          iconLeft: options.showIcons && <FontAwesomeIcon icon={GENDER_ICON[gender]} />,
          label:
            options.showText &&
            translate(`enum.${options.isHuman ? 'genderHuman' : 'gender'}.${gender}`),
        } as SelectOption<GenderEnum>),
    );
};

export const applicationGenderSelectOptions = (
  translate: Translate,
  options: {
    showOther?: boolean;
    showText?: boolean;
    showIcons?: boolean;
    isHuman?: boolean;
  } = {
    showIcons: false,
    showText: true,
    showOther: false,
    isHuman: false,
  },
): SelectOption<GenderEnum | 'none'>[] => {
  const selectOptions = Object.values(GenderEnum)
    .filter((gender: GenderEnum) => (options.showOther ? true : gender !== GenderEnum.OTHER))
    .map(
      (gender) =>
        ({
          value: gender,
          iconLeft: options.showIcons && <FontAwesomeIcon icon={GENDER_ICON[gender]} />,
          label:
            options.showText &&
            translate(`enum.${options.isHuman ? 'genderHuman' : 'gender'}.${gender}`),
        } as SelectOption<GenderEnum | 'none'>),
    );
  selectOptions.push({
    value: 'none',
    iconLeft: options.showIcons && <FontAwesomeIcon icon={GENDER_ICON[GenderEnum.OTHER]} />,
    label: translate('enum.gender.no_preference'),
  } as SelectOption<GenderEnum | 'none'>);
  return selectOptions;
};

export const parentTypeSelectOptions = (
  translate: Translate,
  options: { showText?: boolean; showIcons?: boolean } = {
    showIcons: false,
    showText: true,
  },
): SelectOption<ParentTypeEnum>[] => {
  return Object.values(ParentTypeEnum).map(
    (parentType) =>
      ({
        value: parentType,
        iconLeft: options.showIcons && <FontAwesomeIcon icon={PARENT_TYPE_ICON[parentType]} />,
        label: options.showText && translate(`enum.parentType.${parentType}`),
      } as SelectOption<ParentTypeEnum>),
  );
};

export const binarySelectOptions = (
  translate: Translate,
  options: { showText?: boolean; showIcons?: boolean } = {
    showIcons: false,
    showText: true,
  },
): SelectOption<boolean>[] => {
  return [true, false].map(
    (value) =>
      ({
        value,
        iconLeft: options.showIcons && <FontAwesomeIcon icon={BINARY_ICON(value)} />,
        label: options.showText && translate(`common.buttons.${value ? 'yes' : 'no'}`),
      } as SelectOption<boolean>),
  );
};

export const bornTypeSelectOptions = (
  translate: Translate,
  options: { showText?: boolean; showIcons?: boolean } = {
    showIcons: false,
    showText: true,
  },
): SelectOption<boolean>[] => {
  return [true, false].map(
    (born) =>
      ({
        value: born,
        iconLeft: options.showIcons && <FontAwesomeIcon icon={BORN_TYPE_ICON(born)} />,
        label: options.showText
          ? translate(`common.options.born.${born ? 'true' : 'false'}`)
          : undefined,
      } as SelectOption<boolean>),
  );
};

export const pedigreeSelectOptions = (
  translate: Translate,
  options: { showText?: boolean; showIcons?: boolean; value?: string | null } = {
    showIcons: false,
    showText: true,
    value: '',
  },
): SelectOption<PedigreeType>[] => {
  return [
    'FCI',
    (options.value != null && options.value !== 'FCI' ? options.value : '') ?? '',
    null,
  ].map((pedigreeType) => {
    const presentationType: PedigreeTypePresentation =
      // eslint-disable-next-line no-nested-ternary
      pedigreeType === null ? 'NONE' : pedigreeType === 'FCI' ? 'FCI' : 'OTHER';
    return {
      value: pedigreeType,
      iconLeft: options.showIcons && (
        <FontAwesomeIcon icon={PEDIGREE_TYPE_ICON[presentationType]} />
      ),
      label: options.showText && translate(`enum.pedigreeType.${presentationType}`),
    } as SelectOption<PedigreeType>;
  });
};

export const allLanguageSelectOptions = (
  language: Language,
  languageCodes = LANGUAGE_CODES,
): SelectOption<string>[] => {
  const languageNames = new (Intl as any).DisplayNames([language], { type: 'language' });
  const options = languageCodes.map(
    (code) =>
      ({
        value: code,
        label: capitalize(languageNames.of(code)),
      } as SelectOption<string>),
  );

  return sortSelectOptionByLabel(options);
};

export const allPhonePrefixSelectOptions = (translate: Translate): SelectOption<string>[] => {
  const options = SUPPORTED_PHONE_NUMBERS.map((code) => ({
    value: code,
    label: COUNTRY_CODES_WITH_PHONE_LABELS[code],
    searchValues: [code, translate(`enum.country.${code}`)],
  }));

  return options;
};

export const buyerPhonePrefixSelectOptions = (translate: Translate): SelectOption<string>[] => {
  const options = BUYER_SUPPORTED_PHONE_NUMBERS.map((code) => ({
    value: code,
    label: COUNTRY_CODES_WITH_PHONE_LABELS[code],
    searchValues: [code, translate(`enum.country.${code}`)],
  }));

  return options;
};

export const articleTagsSelectOptions = (translate: Translate): SelectOption<string>[] => {
  const translationsTagObject = translate('common.articleCategoriesTags', {
    returnObjects: true,
  });

  let tagsPerCategory: Record<string, string[]> = mapValues(
    (category) => (category as any)?.split(', '),
    translationsTagObject,
  );

  tagsPerCategory = pickBy((value, key) => value !== undefined && !!value.length, tagsPerCategory);

  const options: SelectOption<string>[] = [];

  for (const category of Object.keys(tagsPerCategory)) {
    for (const tag of tagsPerCategory[category]) {
      // eslint-disable-next-line no-continue
      if (!tag) continue;
      options.push({
        value: tag,
        label: translate(`common.articleTags.${tag}`),
      } as SelectOption<string>);
    }
  }

  return sortSelectOptionByLabel(uniqBy((x) => x.value, options));
};

export const motherRestBetweenLittersOptions = (translate: Translate): SelectOption<string>[] => {
  return [
    { value: 'Between 12 and 15 months', label: translate('common.options.motherRest.12-15') },
    { value: 'Between 15 and 18 months', label: translate('common.options.motherRest.15-18') },
    { value: 'Between 18 and 24 months', label: translate('common.options.motherRest.18-24') },
    { value: '2 years +', label: translate('common.options.motherRest.+24') },
  ];
};

export const breederLevelSelectionOptions = (
  translate: Translate,
  country?: CountryCode,
): SelectOption<string>[] => {
  switch (country) {
    case CountryCode.FR:
      return [
        { value: '4', label: translate('common.options.breederTypes.4') },
        { value: '3', label: translate('common.options.breederTypes.3') },
      ];
    case CountryCode.NL:
      return [
        { value: '5', label: translate('common.options.breederTypes.5') },
        { value: '6', label: translate('common.options.breederTypes.6') },
      ];
    case CountryCode.DE:
      return [
        { value: '7', label: translate('common.options.breederTypes.7') },
        { value: '8', label: translate('common.options.breederTypes.8') },
      ];
    default:
      return [
        { value: '1', label: translate('common.options.breederTypes.1') },
        { value: '2', label: translate('common.options.breederTypes.2') },
        { value: '3', label: translate('common.options.breederTypes.3') },
      ];
  }
};

export const animalSensitivitySelectOptions = (translate: Translate): SelectOption<string>[] => {
  const translations: { [key: string]: any } =
    translate('common.inputs.sensitivity.options', { returnObjects: true }) ?? {};

  const values = Object.values(translations).map((value) => {
    return {
      value,
      label: value,
    } as SelectOption<string>;
  });
  return values;
};

export const enumSelectOptions = <TEnum extends any>(
  translate: Translate,
  translationKey: string,
  enumObject: { [key: string]: string },
  orderAZ?: boolean,
): SelectOption<TEnum>[] => {
  const values = Object.values(enumObject).map((condition) => {
    return {
      value: condition,
      label: translate(`${translationKey}.${condition}`),
    } as SelectOption<TEnum>;
  });

  if (!orderAZ) {
    return values;
  }

  return sortBy('label')(values);
};

export const countrySelectOptions = (translate: Translate) => {
  return SUPPORTED_COUNTRIES.map((country) => ({
    label: translate(`enum.country.${country}`),
    value: country,
  }));
};

export const buyerCountrySelectOptions = (translate: Translate) => {
  return SUPPORTED_BUYER_COUNTRIES.map((country) => ({
    label: translate(`enum.country.${country}`),
    value: country,
  }));
};

export const activatedDeactivatedOptions = (translate: Translate) => {
  return [
    { label: translate('glossary.activated'), value: true },
    { label: translate('glossary.deactivated'), value: false },
  ];
};

export const studAvailableSelectOptions = (translate: Translate) => {
  return [
    { label: translate('glossary.activated'), value: true },
    { label: translate('glossary.deactivated'), value: false },
  ];
};

export const studPriceSelectOptions = (translate: Translate) => {
  return [
    { label: translate('common.options.knownPrice.true'), value: true },
    { label: translate('common.options.knownPrice.false'), value: false },
  ];
};

export const breederRegionOptions = (
  translate: Translate,
  country: CountryCode,
): SelectOption<string>[] => {
  const regions = SUPPORTED_REGIONS[country];
  return regions.map(
    (region) =>
      ({
        value: region,
        label: translate(`enum.regionCode.${region}`),
      } as SelectOption<string>),
  );
};

export const filterRegionOptions = (
  translate: Translate,
  country: CountryCode,
  countriesFromSearch?: CountryCode[],
): SelectOption<string>[] => {
  let regions: string[] = [];
  if (!countriesFromSearch) {
    regions = SUPPORTED_REGIONS[country];
  } else if (Array.isArray(countriesFromSearch)) {
    countriesFromSearch.forEach((item) => {
      regions = regions.concat(SUPPORTED_REGIONS[item]);
    });
  }
  return regions
    .map(
      (region) =>
        ({
          label: translate(`enum.regionCode.${region}`),
          value: region,
        } as SelectOption<string>),
    )
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));
};

export const littersByFemaleOptions: SelectOption<string>[] = [
  { value: '1-2', label: '1-2' },
  { value: '3-5', label: '3-5' },
  { value: '6+', label: '6+' },
];

export const customOrderedAnimalTypeOptions = (t: Translate) => {
  const customOrder = [
    AnimalTypeEnum.DOG,
    AnimalTypeEnum.CAT,
    AnimalTypeEnum.NACSMALL,
    AnimalTypeEnum.NAC,
    AnimalTypeEnum.RABBITS,
    AnimalTypeEnum.SMALLRODENT,
  ];

  const firstItems = customOrder.map((key) => ({
    value: key,
    label: t(`enum.animalType.${key}`),
  }));

  const otherAnimals = Object.keys(AnimalTypeEnum)
    .filter((key) => !customOrder.includes(key as AnimalTypeEnum))
    .map((animalTypeKey) => ({
      value: animalTypeKey as AnimalTypeEnum,
      label: t(`enum.animalType.${animalTypeKey}`),
    }));

  return firstItems.concat(otherAnimals);
};
